import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle, XCircle, Loader, Save, TestTube, Eye, EyeOff } from 'lucide-react';

// Modale de création/édition d'un compte email (extrait de MailSettings).
const MailAccountModal = ({ saving, showImapPassword, setShowImapPassword, showSmtpPassword, setShowSmtpPassword, testing, testResult, editingAccount, accountForm, setAccountForm, admins, successMessage, errorMessage, testConnection, saveEmailAccount, closeAccountModal }) => {
  const { t } = useTranslation('admin');
  return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">
                {editingAccount ? t('mail.mam.editAccount') : t('mail.mam.addAccount')}
              </h3>
            </div>

            {/* Success/Error Messages dans le modal */}
            {successMessage && (
              <div className="mx-6 mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center">
                <CheckCircle className="text-emerald-600 mr-3" size={20} />
                <span className="text-emerald-700">{successMessage}</span>
              </div>
            )}

            {errorMessage && (
              <div className="mx-6 mt-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
                <XCircle className="text-red-600 mr-3" size={20} />
                <span className="text-red-700">{errorMessage}</span>
              </div>
            )}

            <div className="p-6 space-y-4">
              {/* Email & Display Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.emailAddressRequired')}
                  </label>
                  <input
                    type="email"
                    value={accountForm.email_address}
                    onChange={(e) => setAccountForm({...accountForm, email_address: e.target.value})}
                    placeholder={t('mail.mam.emailPlaceholder')}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    disabled={editingAccount}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.displayNameRequired')}
                  </label>
                  <input
                    type="text"
                    value={accountForm.display_name}
                    onChange={(e) => setAccountForm({...accountForm, display_name: e.target.value})}
                    placeholder={t('mail.mam.displayNamePlaceholder')}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('mail.mam.accountType')}
                </label>
                <select
                  value={accountForm.account_type}
                  onChange={(e) => setAccountForm({...accountForm, account_type: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  disabled={editingAccount}
                >
                  <option value="individual">{t('mail.mam.optIndividual')}</option>
                  <option value="shared">{t('mail.mam.optShared')}</option>
                </select>
              </div>

              {/* Individual Account - Assign Admin */}
              {accountForm.account_type === 'individual' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.assignToAdmin')}
                  </label>
                  <select
                    value={accountForm.assigned_admin_id}
                    onChange={(e) => setAccountForm({...accountForm, assigned_admin_id: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t('mail.mam.selectAdmin')}</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>
                        {admin.first_name} {admin.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Shared Account - Authorized Admins */}
              {accountForm.account_type === 'shared' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.authorizedAdmins')}
                  </label>
                  <div className="border border-slate-200 rounded-xl p-4 max-h-40 overflow-y-auto space-y-2">
                    {admins.map(admin => (
                      <div key={admin.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`admin-${admin.id}`}
                          checked={accountForm.authorized_admin_ids.includes(admin.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAccountForm({
                                ...accountForm,
                                authorized_admin_ids: [...accountForm.authorized_admin_ids, admin.id]
                              });
                            } else {
                              setAccountForm({
                                ...accountForm,
                                authorized_admin_ids: accountForm.authorized_admin_ids.filter(id => id !== admin.id)
                              });
                            }
                          }}
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <label htmlFor={`admin-${admin.id}`} className="ml-2 text-sm text-slate-700">
                          {admin.first_name} {admin.last_name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Configuration IMAP Serveur */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center">
                  <Mail className="mr-2 text-blue-600" size={18} />
                  {t('mail.mam.imapConfig')}
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('mail.mam.imapServerRequired')}
                    </label>
                    <input
                      type="text"
                      value={accountForm.imap_host}
                      onChange={(e) => setAccountForm({...accountForm, imap_host: e.target.value})}
                      placeholder="imap.gmail.com"
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('mail.mam.imapPort')}
                    </label>
                    <input
                      type="number"
                      value={accountForm.imap_port || ''}
                      onChange={(e) => setAccountForm({...accountForm, imap_port: e.target.value ? parseInt(e.target.value) : ''})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="flex items-center mt-3">
                  <input
                    type="checkbox"
                    id="imap_ssl"
                    checked={accountForm.imap_use_ssl}
                    onChange={(e) => setAccountForm({...accountForm, imap_use_ssl: e.target.checked})}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="imap_ssl" className="ml-2 text-sm text-slate-700">
                    {t('mail.mam.useSsl')}
                  </label>
                </div>
              </div>

              {/* IMAP Credentials */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.imapUsernameRequired')}
                  </label>
                  <input
                    type="text"
                    value={accountForm.imap_username}
                    onChange={(e) => setAccountForm({...accountForm, imap_username: e.target.value})}
                    placeholder="contact@asarcsg.com"
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.imapPasswordRequired')}
                  </label>
                  <div className="relative">
                    <input
                      type={showImapPassword ? "text" : "password"}
                      value={accountForm.imap_password}
                      onChange={(e) => setAccountForm({...accountForm, imap_password: e.target.value})}
                      placeholder={editingAccount ? t('mail.mam.unchanged') : ''}
                      className="w-full px-4 py-2 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowImapPassword(!showImapPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showImapPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Configuration SMTP Serveur */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center">
                  <Mail className="mr-2 text-emerald-600" size={18} />
                  {t('mail.mam.smtpConfig')}
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('mail.mam.smtpServerRequired')}
                    </label>
                    <input
                      type="text"
                      value={accountForm.smtp_host}
                      onChange={(e) => setAccountForm({...accountForm, smtp_host: e.target.value})}
                      placeholder="smtp.gmail.com"
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('mail.mam.smtpPort')}
                    </label>
                    <input
                      type="number"
                      value={accountForm.smtp_port || ''}
                      onChange={(e) => setAccountForm({...accountForm, smtp_port: e.target.value ? parseInt(e.target.value) : ''})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="flex items-center mt-3">
                  <input
                    type="checkbox"
                    id="smtp_ssl"
                    checked={accountForm.smtp_use_ssl}
                    onChange={(e) => setAccountForm({...accountForm, smtp_use_ssl: e.target.checked})}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="smtp_ssl" className="ml-2 text-sm text-slate-700">
                    {t('mail.mam.useSsl')}
                  </label>
                </div>
              </div>

              {/* SMTP Credentials */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.smtpUsernameRequired')}
                  </label>
                  <input
                    type="text"
                    value={accountForm.smtp_username}
                    onChange={(e) => setAccountForm({...accountForm, smtp_username: e.target.value})}
                    placeholder="contact@asarcsg.com"
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('mail.mam.smtpPasswordRequired')}
                  </label>
                  <div className="relative">
                    <input
                      type={showSmtpPassword ? "text" : "password"}
                      value={accountForm.smtp_password}
                      onChange={(e) => setAccountForm({...accountForm, smtp_password: e.target.value})}
                      placeholder={editingAccount ? t('mail.mam.unchanged') : ''}
                      className="w-full px-4 py-2 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showSmtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Email Signature */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('mail.mam.signatureHtml')}
                </label>
                <textarea
                  value={accountForm.email_signature}
                  onChange={(e) => setAccountForm({...accountForm, email_signature: e.target.value})}
                  placeholder={t('mail.mam.signaturePlaceholder')}
                  rows="4"
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={accountForm.is_active}
                  onChange={(e) => setAccountForm({...accountForm, is_active: e.target.checked})}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-slate-700">
                  {t('mail.mam.accountActive')}
                </label>
              </div>
            </div>

            {/* Test Results */}
            {testResult && (
              <div className="px-6 pb-6">
                <h4 className="font-semibold text-slate-900 mb-4">{t('mail.mam.testResults')}</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl border ${
                    testResult.imap.success 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center mb-2">
                      {testResult.imap.success ? (
                        <CheckCircle className="text-emerald-600 mr-2" size={20} />
                      ) : (
                        <XCircle className="text-red-600 mr-2" size={20} />
                      )}
                      <span className="font-semibold">IMAP</span>
                    </div>
                    <p className="text-sm text-slate-700">{testResult.imap.message}</p>
                  </div>

                  <div className={`p-4 rounded-xl border ${
                    testResult.smtp.success 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center mb-2">
                      {testResult.smtp.success ? (
                        <CheckCircle className="text-emerald-600 mr-2" size={20} />
                      ) : (
                        <XCircle className="text-red-600 mr-2" size={20} />
                      )}
                      <span className="font-semibold">SMTP</span>
                    </div>
                    <p className="text-sm text-slate-700">{testResult.smtp.message}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-6 border-t border-slate-200 flex items-center justify-between">
              <button
                onClick={testConnection}
                disabled={testing || saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {testing ? <Loader className="animate-spin" size={16} /> : <TestTube size={16} />}
                <span>{testing ? t('mail.mam.testInProgress') : t('mail.mam.testConnection')}</span>
              </button>

              <div className="flex items-center space-x-3">
                <button
                  onClick={closeAccountModal}
                  className="px-6 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  {t('mail.cancelBtn')}
                </button>
                <button
                  onClick={saveEmailAccount}
                  disabled={saving}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  {saving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
                  <span>{saving ? t('mail.mam.savingBtn') : t('mail.mam.saveBtn')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
  );
};

export default MailAccountModal;
